/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-12-05 15:45:31
 * @ Description: Dialog for creating a new org
 */

import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'

// Centralized lodash imports - much cleaner!
import {
  map,
  toLower,
  upperFirst,
  startCase,
  filter,
  includes,
} from '@/utils/lodash'

import {
  Grid,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemButton,
  MenuItem,
  Step,
  Stepper,
  StepLabel,
  TextField,
  Typography,
  Radio,
  InputAdornment,
  CircularProgress,
  List,
  Checkbox,
  Menu,
  Select,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'

import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Check from '@mui/icons-material/Check'
import StepConnector from '@mui/material/StepConnector'
import AddIcon from '@mui/icons-material/Add'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import BusinessIcon from '@mui/icons-material/Business'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { useMutation, useQuery } from '@apollo/client/react'
import OrgPlanAccordion from './OrgPlanAccordion'
import { GET_SUBSCRIPTION_PLAN } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'
import {
  CHECK_SIGN_UP,
  GET_COUPON,
} from '@/housefolios-components/CreateAccount/CreateAccountCard/mutations'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import PaymentForm from '@/housefolios-components/CreateAccount/PaymentForm'
import {
  CREATE_ORGANIZATION,
  CREATE_PARTNER_ORGANIZATION,
} from '../mutations'
import withSession from '@/housefolios-components/Session/withSession'
import { GET_ME } from '@/housefolios-components/Session/queries'

import { enqueueSnackbar } from 'notistack'
import { Add, ArrowForward } from '@mui/icons-material'
import { UPDATE_USER } from '@/housefolios-components/PersonalSettings/mutations'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { useNavigate } from 'react-router'
import useAffiliate from '@/hooks/useAffiliate'
import ContactDialog from '@/housefolios-components/Dialogs/ContactSupport'
import { LaunchpadButton } from '@/housefolios-components/shared'
import { wizardStepIconSx } from '@/theme/skinSx'

function StepIcon(props) {
  const { active, completed } = props

  const icons = {
    1: <BusinessIcon />,
    2: <RadioButtonCheckedIcon />,
    3: <CreditCardIcon />,
  }

  return (
    <Box sx={wizardStepIconSx({ active, completed })}>
      {completed ? (
        <Check />
      ) : (
        icons[String(props.icon)]
      )}
    </Box>
  )
}

StepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.node,
}

function getSteps() {
  return ['Organization Details', 'Select Plan', 'Payment Details']
}

function CreateNewOrgComponent(props) {
  const [activeStep, setActiveStep] = useState(0)
  const [name, setName] = useState('')
  const [orgType, setOrgType] = useState('Personal Organization')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedPlan, setSelectedPlan] = useState('')
  const [subscriptionPlan, setSubscriptionPlan] = useState('')
  const [planPrice, setPlanPrice] = useState(0)
  const [planBilling, setPlanBilling] = useState('')

  const [openContact, setOpenContact] = useState(false)

  const [{ affiliate, urlAffiliate }] = useAffiliate({
    memberValue: props?.session?.me?.activeOrg?.member,
  })

  const isCoached = includes(subscriptionPlan, 'coaching')

  let navigate = useNavigate()

  const [paymentIntervals, setPaymentIntervals] = useState(1)

  const [token, setToken] = useState('')
  const [recharge, setRecharge] = useState(false)
  // const [element, setElement] = useState(null)
  const [resetToken, setResetToken] = useState(() => {
    return null
  })
  const [coupon, setCoupon] = useState('')
  //const [couponValid, setCouponValid] = useState(null)
  const [couponPercent, setCouponPercent] = useState(null)
  const [couponAmount, setCouponAmount] = useState(null)
  const [circle, setCircle] = useState(false)

  const [newDefault, setNewDefault] = useState(false)

  const [createOrganization] = useMutation(CREATE_ORGANIZATION)
  const [createPartnerOrg] = useMutation(CREATE_PARTNER_ORGANIZATION)
  const [updateUser] = useMutation(UPDATE_USER)

  const [newOrgId, setNewOrgId] = useState(null)

  const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY
  const stripePromise = props.disabled
    ? null
    : loadStripe(stripePublicKey)

  const handleRechargeToggle = () => {
    setRecharge(!recharge)
    setToken('')
    resetToken()
  }

  const createToken = async (stripe, cardElement, nameOnCard) => {
    if (!nameOnCard) setToken('')
    else {
      await stripe
        .createToken(cardElement, {
          name: nameOnCard,
        })
        .then((res) => {
          var errorElement = document.getElementById('card-errors')
          if (res.error) {
            setToken('')
            if (errorElement)
              errorElement.textContent = res.error.message
          } else {
            setToken(res.token)
            if (errorElement) errorElement.textContent = null
          }
        })
    }
  }

  const [getCoupon] = useMutation(GET_COUPON)

  useEffect(() => {
    if (includes(subscriptionPlan, 'group-coaching')) {
      let applyCoupon = ''
      if (paymentIntervals == 1) {
        applyCoupon = 'GTzjUUmX'
      } else if (paymentIntervals == 2) {
        applyCoupon = 'CxxvDO0k'
      } else if (paymentIntervals == 3) {
        applyCoupon = 'arM9K3su'
      }
      setCoupon(applyCoupon)
      checkCoupon(applyCoupon)
    } else {
      setCoupon('')
      checkCoupon('')
    }
  }, [subscriptionPlan, paymentIntervals])

  const checkCoupon = async (coupon) => {
    //if (!coupon) setCouponValid(null)
    if (coupon)
      await getCoupon({
        variables: {
          code: coupon,
        },
      })
        .then(async ({ data  }) => {
          if (
            !data.getCoupon.maxRedemptions ||
            data.getCoupon.maxRedemptions >
              data.getCoupon.timesRedeemed
          ) {
            //setCouponValid(true)
            setCouponPercent(data.getCoupon.percentOff)
            setCouponAmount(data.getCoupon.amountOff)
          } else {
            enqueueSnackbar(`Promo Code not valid`, {
              variant: 'error',
              autoHideDuration: 3000,
            })
            //setCouponValid(false)
          }
        })
        .catch(async ({ error  }) => {
          enqueueSnackbar(`Promo Code not valid`, {
            variant: 'error',
            autoHideDuration: 3000,
          })
          //setCouponValid(false)
          setCoupon('')
        })
    else {
      setCoupon('')
      setCouponPercent('')
      setCouponAmount('')
    }
  }

  const steps = getSteps()

  const handleNext = async () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleReset = () => {
    setActiveStep(0)
  }

  let entryPlanId = ''
  let entrySubscriptionPlan = {}
  const entryPlanResults = useQuery(GET_SUBSCRIPTION_PLAN, {
    variables: {
      subscriptionPlan:
        `${subscriptionPlan}-entry-fee` +
        (paymentIntervals > 1 ? `-${paymentIntervals}-payments` : ''),
    },
    errorPolicy: 'ignore',
  })
  if (isCoached) {
    const entryData = (entryPlanResults || {}).data || {}
    entrySubscriptionPlan = entryData.subscriptionPlan || {}
    entryPlanId = entrySubscriptionPlan.planId
  }

  const handleAdd = () => {
    if (!token && !recharge) return
    setCircle(true)
    createOrganization({
      variables: {
        organizationInput: {
          name: name,
          email: toLower(props.session.me.email),
          stripeToken: token,
          stripePlanId: selectedPlan,
          coupon,
          stripeEntryPlanId: entryPlanId,
          paymentIntervals: isCoached ? paymentIntervals : 0,
        },
      },
      refetchQueries: [
        {
          query: GET_ME,
        },
      ],
    })
      .then(({ data  }) => {
        localStorage.setItem('token', data.createOrganization.token)
        setNewOrgId(data.createOrganization.orgId)
        //if (props.history) props.history.push("/launchpad")
        //window.location.reload()
        handleNext()
        setCircle(false)
      })
      .catch((error) => {
        // if (element) element.clear()
        var errorElement = document.getElementById('card-errors')
        resetToken()
        if (error && errorElement) {
          errorElement.textContent =
            error.message.slice(0, 21) === 'GraphQL error: Error:'
              ? error.message.slice(21)
              : null
        }
        setCircle(false)
      })
  }

  const handleAddPartner = () => {
    setCircle(true)
    createPartnerOrg({
      variables: {
        userInput: {
          profile: {
            firstname: firstName,
            lastname: lastName,
          },
          email: toLower(email),
          // userType,
          organization: name,
          phone: phoneNumber,
          // address: billingAddress,
          // city,
          // state,
          // zip: postalCode,
          stripePlanId: selectedPlan,
          coupon,
        },
      },
      refetchQueries: [
        {
          query: GET_ME,
        },
      ],
    })
      .then(({ data  }) => {
        // localStorage.setItem(
        //   'token',
        //   data.createOrganization.token,
        // )
        //if (props.history) props.history.push("/launchpad")
        //window.location.reload()
        handleCloseCreateNewDialog()
        setCircle(false)
      })
      .catch((error) => {
        console.log(error)
        // if (element) element.clear()
        setCircle(false)
      })
  }

  const [openCreateNewOrgDialog, setCreateNewOrgDialog] =
    useState(false)
  const handleOpenCreateNewDialog = () => {
    if (props.session?.me?.activeOrg?.isRealeflow) {
      setOpenContact(true)
    } else {
      setCreateNewOrgDialog(true)
    }
  }
  const handleCloseCreateNewDialog = () => {
    setCreateNewOrgDialog(false)
    handleReset()
    setName('')
    setOrgType('Personal Organization')
    setSelectedPlan('')
    setSubscriptionPlan('')
    setPlanPrice(0)
    setPlanBilling('')
    setToken('')
    //setElement(null)
    setCoupon('')
    //setCouponValid(null)
    setCouponPercent(null)
    setCouponAmount(null)
  }

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const currencyFormat2 = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const pricingPlans = props.session.me.activeOrg.currentlyFree
    ? [
        {
          nameMonthly: 'free-monthly',
          nameYearly: 'free-yearly',
          label: 'Free',
          subHeaderMonthly: '',
          subHeaderYearly: '',
          search: '*Nationwide property search',
          analyze: '*Analyze off-market properties',
          comp: '*Sold comp data',
        },
        {
          nameMonthly: 'investor-monthly',
          nameYearly: 'investor-yearly',
          label: 'Investor',
          subHeaderMonthly: 'Monthly Plan',
          subHeaderYearly: 'Annual Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
        },
        {
          nameMonthly: 'growth-monthly',
          nameYearly: 'growth-yearly',
          nameMonthlyPartner: 'partner-growth-monthly',
          partnerWarning:
            'Minimum total price of $250 for all partner orgs',
          label: 'Growth',
          subHeaderMonthly: 'Monthly Plan',
          subHeaderYearly: 'Annual Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
          //housefoliosMarketplace: 'List on Housefolios marketplace',
        },
        {
          nameMonthly: 'pro-monthly',
          nameYearly: 'pro-yearly',
          label: 'Pro',
          subHeaderMonthly: 'Monthly Plan',
          subHeaderYearly: 'Annual Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
          //housefoliosMarketplace: 'List on Housefolios marketplace',
        },
      ]
    : [
        {
          nameMonthly: 'investor-monthly',
          nameYearly: 'investor-yearly',
          label: 'Investor',
          subHeaderMonthly: 'Monthly Plan',
          subHeaderYearly: 'Annual Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
        },
        {
          nameMonthly: 'growth-monthly',
          nameYearly: 'growth-yearly',
          nameMonthlyPartner: 'partner-growth-monthly',
          partnerWarning:
            'Minimum total price of $250 for all partner orgs',
          label: 'Growth',
          subHeaderMonthly: 'Monthly Plan',
          subHeaderYearly: 'Annual Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
          //housefoliosMarketplace: 'List on Housefolios marketplace',
        },
        {
          nameMonthly: 'pro-monthly',
          nameYearly: 'pro-yearly',
          label: 'Pro',
          subHeaderMonthly: 'Monthly Plan',
          subHeaderYearly: 'Annual Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
          //housefoliosMarketplace: 'List on Housefolios marketplace',
        },
        {
          nameMonthly: 'group-coaching',
          label: 'Group Coaching',
          subHeaderMonthly: 'Plan',
          subHeaderYearly: 'Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
          groupCoaching: 'Weekly Group Coaching',
          //housefoliosMarketplace: 'List on Housefolios marketplace',
        },
        {
          nameMonthly: 'personalized-coaching',
          label: 'Personalized Coaching',
          subHeaderMonthly: 'Plan',
          subHeaderYearly: 'Plan',
          search: 'Nationwide property search',
          analyze: 'Analyze off-market properties',
          comp: 'Sold comp data',
          groupCoaching: 'Weekly Group Coaching',
          personalizedCoaching: 'Monthly One on One Coaching',
          //housefoliosMarketplace: 'List on Housefolios marketplace',
        },
      ]

  return (
    <>
      {props.orgSettings && (
        <MenuItem onClick={handleOpenCreateNewDialog}>
          <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: 10, width: '1rem' }} sx={{ fontSize: "1.15rem !important" }} />
          <Typography>Create Organization</Typography>
        </MenuItem>
      )}
      {props.listItem && (
        <ListItemButton
          key="newOrg"
          onClick={handleOpenCreateNewDialog}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 2,
            minHeight: 0,
            bgcolor: '#fff',
            '&:hover': { bgcolor: 'grey.100' },
            'html[data-color-mode="dark"] &': {
              bgcolor: '#2a2a2a',
              '&:hover': { bgcolor: '#333' },
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#070919',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              'html[data-color-mode="dark"] &': {
                bgcolor: '#4191ff',
              },
            }}
          >
            <AddIcon style={{ color: 'white' }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 13,
                color: '#070919',
                'html[data-color-mode="dark"] &': {
                  color: 'rgba(255, 255, 255, 0.87)',
                },
              }}
            >
              Create New Organization
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                mt: 0.25,
                'html[data-color-mode="dark"] &': {
                  color: 'rgba(255, 255, 255, 0.68)',
                },
              }}
            >
              Manage multiple orgs in 1 account
            </Typography>
          </Box>
        </ListItemButton>
      )}
      {props.headerDot && (
        <Grid size={{ sm: 6 }}>
          <LaunchpadButton
            title="Organization"
            subtitle="Starting new project?"
            icon={['far', 'building']}
            onClick={handleOpenCreateNewDialog}
            disabled={props.disabled}
          />
        </Grid>
      )}
      {props.title && (
        <Button
          variant="contained"
          onClick={handleOpenCreateNewDialog}
          sx={[{
            boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
            color: "#fff",
            backgroundColor: "#368cff",
            backgroundImage: "none",
            borderColor: "#368cff",
            "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
            "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
            "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
            "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
            "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
          }, { m: '0.5rem', fontWeight: 'bolder' }]}
          >
          <Add style={{ color: 'white' }} />
          <Box component="span" style={{ fontWeight: 600 }} sx={{
            display: "inline-block",
            "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
            "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
          }}>
            Create Organization
          </Box>
        </Button>
      )}
      {props.deactive && (
        <Button
          href={null}
          onClick={handleOpenCreateNewDialog}
          size="large"
          sx={{
            boxShadow: "0 0.25rem 0.55rem rgba(65, 145, 255, 0.35)",
            color: "#fff",
            backgroundColor: "#4191ff",
            backgroundImage: "none",
            borderColor: "#4191ff",
            "&:not(:disabled):hover": { backgroundColor: "#4191ff", color: "#fff" },
            "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0e73ff" },
            "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(65, 145, 255, 0.4), 0 0.0625rem 0.385rem rgba(65, 145, 255, 0.54)" },
            "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
            "@media (min-width: 576px)": { display: "inline-block" },
            display: "block",
          }}
          >
          <Box component="span" sx={{
            display: "inline-block",
            "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
            "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
          }}>Choose Plan</Box>
          <span style={{ display: "inline-flex" }}>
            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
          </span>
        </Button>
      )}

      <Dialog
        maxWidth="lg"
        open={openCreateNewOrgDialog}
        onClose={handleCloseCreateNewDialog}
        fullWidth
        onKeyDown={(event) => event.stopPropagation()}
      >
        <ValidatorForm
          style={{
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onSubmit={
            activeStep === steps.length - 1
              ? orgType === 'Partner Organization'
                ? handleAddPartner
                : handleAdd
              : handleNext
          }
        >
          <BlockUi
            blocking={circle}
            loader={
              <PacmanLoader color={'var(--primary)'} loading={true} />
            }
            message={
              <Box sx={{ color: '#368cff' }}>
                Creating Organization
              </Box>
            }
          >
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogContent style={{ padding: 0 }}>
              {/* <Card>
                <div>
                  <div>
                    <Typography variant='h6'>Create New Organization</Typography>
                  </div>
                </div> */}
              <div>
                <Box sx={{ backgroundColor: '#f8f9ff', mb: '1rem', 'html[data-color-mode="dark"] &': { backgroundColor: '#2a2a2a !important' } }}>
                  <Stepper
                    style={{ padding: 24 }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "transparent",
                      "& .MuiStepConnector-alternativeLabel": { top: "50%", marginTop: -18 },
                      "& .MuiStepLabel-alternativeLabel": { fontSize: "1rem", color: "#4f5062", "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.68)" } },
                      "& .MuiStepLabel-label.Mui-active": { color: "#368cff", fontWeight: 700 },
                      "& .MuiStepLabel-label.Mui-completed": { color: "#1bc943", fontWeight: 700 },
                    }}
                    alternativeLabel
                    activeStep={activeStep}
                    connector={<StepConnector />}
                  >
                    {steps.map((label) => (
                      <Step key={label}>
                        <StepLabel
                          slots={{
                            stepIcon: StepIcon,
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                {activeStep === steps.length ? (
                  <Box sx={{ textAlign: 'center', p: '3rem' }}>
                    {circle && <CircularProgress />}
                    {!circle && (
                      <Box sx={{ textAlign: 'center', p: '3rem' }}>
                        <Box sx={[{ borderRadius: '50%', m: 0 }, {
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: "0.25rem",
                          position: "relative",
                          fontSize: "1.2rem",
                          transition: "all 0.2s ease-in-out",
                          "&:last-child": { mr: 0, marginRight: 0 },
                          "& .badge": { position: "absolute", right: 0, bottom: 0 },
                          "& svg, & .svg-inline--fa": { display: "block" },
                          marginRight: "0.25rem",
                        }]}>
                          <Box sx={[{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: 0,
                            backgroundColor: "rgb(89.8709677419%, 97.8153067679%, 93.0487033523%) !important",
                            height: "130px !important",
                            width: "130px !important",
                            lineHeight: "130px !important",
                            p: 0,
                            borderRadius: "50%",
                            color: "#1bc943",
                            m: 0,
                          }, {
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: "0.25rem",
                            position: "relative",
                            fontSize: "1.2rem",
                            transition: "all 0.2s ease-in-out",
                            "&:last-child": { mr: 0, marginRight: 0 },
                            "& .badge": { position: "absolute", right: 0, bottom: 0 },
                            "& svg, & .svg-inline--fa": { display: "block" },
                            marginRight: "0.25rem",
                          }]}>
                            <FontAwesomeIcon icon={['far', 'check']} style={{ display: 'flex', alignSelf: 'center', }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
                          </Box>
                        </Box>
                        <Box
                          component="h4"
                          sx={{ fontWeight: 700, mt: '1.5rem' }}
                        >
                          New organization created
                        </Box>
                        <Box component="p" sx={[{ mb: 0, color: '#a6a6b9' }, { fontSize: "1.15rem !important" }]}>
                          You can switch between organizations by
                          clicking on your avatar at the top right
                        </Box>
                        <Box sx={{ pt: '1.5rem' }}>
                          <Button
                            size="large"
                            endIcon={<ArrowForward  sx={[{
                              boxShadow: "0 0.25rem 0.55rem rgba(27, 201, 67, 0.35)",
                              color: "#fff",
                              backgroundColor: "#1bc943",
                              backgroundImage: "none",
                              borderColor: "#1bc943",
                              "&:not(:disabled):hover": { backgroundColor: "#1bc943", color: "#fff" },
                              "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#159c34" },
                              "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(27, 201, 67, 0.4), 0 0.0625rem 0.385rem rgba(27, 201, 67, 0.54)" },
                              "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                            }, {
                              transition: "all 0.2s ease-in-out !important",
                              transform: "scale(1) !important",
                              "&:hover": { transform: "scale(1.12)" },
                            }]}/>}
                            onClick={(e) => {
                            updateUser({
                            variables: {
                            userInput: {
                            organization: newOrgId,
                            defaultOrg: newDefault
                            ? newOrgId
                            : null,
                            },
                            },
                            refetchQueries: [
                            {
                            query: GET_ME,
                            },
                            {
                            query: GET_PORTFOLIOS,
                            },
                            ],
                            }).then(async ({ data  }) => {
                            localStorage.setItem(
                            'token',
                            data.updateUser.token,
                            )
                            if (navigate) navigate('/launchpad')
                            window.location.reload()
                            })
                            e.preventDefault()
                            }}
                            sx={{
                            fontWeight: 700,
                            borderRadius: '0.65rem',
                            mx: '0.25rem',
                            }}
                            >
                            <Box component="span" sx={{
                              display: "inline-block",
                              "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                              "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                            }}>
                              Go to new organization
                            </Box>
                          </Button>
                        </Box>
                        <div style={{ marginTop: 10 }}>
                          <FormControlLabel
                            onChange={() =>
                              setNewDefault(!newDefault)
                            }
                            control={
                              <Checkbox
                                checked={newDefault}
                                name="default"
                              />
                            }
                            label="Make New Org Your Default Org"
                          />
                        </div>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <div>
                    {activeStep === 0 && (
                      <Container>
                        <Box sx={{ p: '1.5rem' }}>
                          <Box component="h5" sx={[{ mb: '0.25rem', fontWeight: 700 }, { fontSize: "1.265rem !important" }]}>
                            Organization Details
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              color: 'rgba(7, 9, 25, 0.5)',
                              'html[data-color-mode="dark"] &': { color: 'rgba(255, 255, 255, 0.68)' },
                              mb: '1.5rem',
                            }}
                          >
                            Complete the fields below to setup your
                            new organization
                          </Box>
                          <Grid container spacing={4}>
                            {props.session.me.activeOrg
                              .stripeCustomerID &&
                              props.session.me.activeOrg
                                .southernImpressions && (
                                <Grid size={12}>
                                  <TextField
                                    name="orgType"
                                    select
                                    fullWidth={true}
                                    variant="outlined"
                                    label="Organization Type"
                                    value={orgType}
                                    onChange={(event) => {
                                      setOrgType(event.target.value)
                                      setRecharge(false)
                                    }}
                                  >
                                    <MenuItem
                                      value="Personal Organization"
                                      key="Personal Organization"
                                    >
                                      Personal Organization
                                    </MenuItem>
                                    <MenuItem
                                      value="Partner Organization"
                                      key="Partner Organization"
                                    >
                                      Partner Organization
                                    </MenuItem>
                                  </TextField>
                                </Grid>
                              )}
                            <Grid size={12}>
                              <TextValidator
                                fullWidth
                                label="Organization Name*"
                                variant="outlined"
                                autoFocus
                                value={name}
                                onChange={(event) =>
                                  setName(event.target.value)
                                }
                                validators={[
                                  'required',
                                  'maxStringLength: 20',
                                ]}
                                errorMessages={[
                                  'This field is required',
                                  'You cannot exceed 20 characters',
                                ]}
                              />
                            </Grid>
                            {orgType === 'Partner Organization' && (
                              <>
                                <Grid size={12}>
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                  >
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                      <TextValidator
                                        fullWidth={true}
                                        variant="outlined"
                                        placeholder="First Name"
                                        label="First Name"
                                        value={firstName}
                                        onChange={(event) =>
                                          setFirstName(
                                            event.target.value,
                                          )
                                        }
                                        validators={[
                                          'required',
                                          'maxStringLength: 20',
                                          'matchRegexp:^[A-Z|a-z]+$',
                                        ]}
                                        errorMessages={[
                                          'This field is required',
                                          'You cannot exceed 20 characters',
                                          'This field only allows characters in the alphabet',
                                        ]}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                      <TextValidator
                                        fullWidth={true}
                                        variant="outlined"
                                        placeholder="Last Name"
                                        label="Last Name"
                                        value={lastName}
                                        onChange={(event) =>
                                          setLastName(
                                            event.target.value,
                                          )
                                        }
                                        validators={[
                                          'required',
                                          'maxStringLength: 20',
                                          'matchRegexp:^[A-Z|a-z]+$',
                                        ]}
                                        errorMessages={[
                                          'This field is required',
                                          'You cannot exceed 20 characters',
                                          'This field only allows characters in the alphabet',
                                        ]}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid size={12}>
                                  <TextValidator
                                    fullWidth={true}
                                    variant="outlined"
                                    placeholder="Phone Number"
                                    label="Phone Number"
                                    value={phoneNumber}
                                    onChange={(event) =>
                                      setPhoneNumber(
                                        event.target.value,
                                      )
                                    }
                                    validators={[
                                      'required',
                                      //eslint-disable-next-line
                                      'matchRegexp:^[0-9()-]+$',
                                    ]}
                                    errorMessages={[
                                      'This field is required',
                                      'Not a valid phone number',
                                    ]}
                                  />
                                </Grid>
                                <Grid size={12}>
                                  <TextValidator
                                    fullWidth={true}
                                    variant="outlined"
                                    placeholder="Email Address"
                                    label="Email Address"
                                    value={email}
                                    onChange={(event) =>
                                      setEmail(event.target.value)
                                    }
                                    validators={[
                                      'required',
                                      'maxStringLength: 50',
                                      'isEmail',
                                    ]}
                                    errorMessages={[
                                      'This field is required',
                                      'You cannot exceed 50 characters',
                                      'Not a valid email address',
                                    ]}
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Box>
                      </Container>
                    )}
                    {activeStep === 1 && (
                      <Container>
                        <Box sx={{ p: '1.5rem' }}>
                          <Box component="h5" sx={[{ mb: '0.25rem', fontWeight: 700 }, { fontSize: "1.265rem !important" }]}>
                            Monthly Plans
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              color: 'rgba(7, 9, 25, 0.5)',
                              'html[data-color-mode="dark"] &': { color: 'rgba(255, 255, 255, 0.68)' },
                              mb: '1.5rem',
                            }}
                          >
                            Select a monthly plan or save 20% with
                            annual billing
                          </Box>
                          <Grid container>
                            <Grid size={12}>
                              <Box sx={{ marginBottom: "48px" }}>
                                {map(
                                  filter(pricingPlans, (plan) =>
                                    orgType === 'Partner Organization'
                                      ? plan.nameMonthlyPartner
                                      : plan.nameMonthly,
                                  ),
                                  (plan) => (
                                    <OrgPlanAccordion
                                      key={
                                        orgType ===
                                        'Partner Organization'
                                          ? plan.nameMonthlyPartner
                                          : plan.nameMonthly
                                      }
                                      plan={plan}
                                      orgType={orgType}
                                      billing="monthly"
                                      selectedPlan={selectedPlan}
                                      setSelectedPlan={
                                        setSelectedPlan
                                      }
                                      setSubscriptionPlan={
                                        setSubscriptionPlan
                                      }
                                      setPlanPrice={setPlanPrice}
                                      setPlanBilling={setPlanBilling}
                                      currencyFormat={currencyFormat}
                                    />
                                  ),
                                )}
                              </Box>
                            </Grid>
                          </Grid>

                          <Box component="h5" sx={[{ mb: '0.25rem', fontWeight: 700 }, { fontSize: "1.265rem !important" }]}>
                            Save with Annual Plans
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              color: 'rgba(7, 9, 25, 0.5)',
                              'html[data-color-mode="dark"] &': { color: 'rgba(255, 255, 255, 0.68)' },
                              mb: '1.5rem',
                            }}
                          >
                            Save 20% with annual billing
                          </Box>
                          <Grid container>
                            <Grid size={12}>
                              <Box sx={{ marginBottom: "48px" }}>
                                {map(
                                  filter(pricingPlans, (plan) =>
                                    orgType === 'Partner Organization'
                                      ? plan.nameYearlyPartner
                                      : plan.nameYearly,
                                  ),
                                  (plan) => (
                                    <OrgPlanAccordion
                                      key={plan.nameYearly}
                                      plan={plan}
                                      orgType={orgType}
                                      billing="yearly"
                                      selectedPlan={selectedPlan}
                                      setSelectedPlan={
                                        setSelectedPlan
                                      }
                                      setSubscriptionPlan={
                                        setSubscriptionPlan
                                      }
                                      setPlanPrice={setPlanPrice}
                                      setPlanBilling={setPlanBilling}
                                      currencyFormat={currencyFormat}
                                    />
                                  ),
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Container>
                    )}
                    {activeStep === 2 && (
                      <Container>
                        <Box sx={{ p: '1.5rem' }}>
                          <Grid
                            container
                            direction="row"
                            spacing={4}
                            sx={{
                              alignItems: 'flex-start',
                            }}
                          >
                            {props.session.me.activeOrg
                              .stripeCustomerID &&
                              orgType !== 'Partner Organization' && (
                                <Grid
                                  size={12}
                                  style={{ width: '100%' }}
                                >
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={2}
                                    style={{ marginBottom: 10 }}
                                    sx={{
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Grid>
                                      <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={
                                          recharge
                                            ? 'existing'
                                            : 'new'
                                        }
                                        onChange={(_event, next) => {
                                          if (next == null) return
                                          if (
                                            (next === 'existing') !==
                                            recharge
                                          ) {
                                            handleRechargeToggle()
                                          }
                                        }}
                                        aria-label="Card source"
                                        sx={{
                                          borderRadius: "0.65rem",
                                          "& .MuiToggleButton-root": {
                                            textTransform: "none",
                                            fontWeight: 700,
                                            px: 2,
                                            py: 0.75,
                                            color: "#4f5062",
                                            borderColor: "#e6e7f1",
                                            bgcolor: "#fff",
                                          },
                                          "& .MuiToggleButton-root.Mui-selected": {
                                            bgcolor: "#368cff",
                                            color: "#fff",
                                            borderColor: "#368cff",
                                            "&:hover": { bgcolor: "#1a6fd4" },
                                          },
                                        }}
                                      >
                                        <ToggleButton value="new">
                                          New Card
                                        </ToggleButton>
                                        <ToggleButton value="existing">
                                          Existing Card
                                        </ToggleButton>
                                      </ToggleButtonGroup>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )}
                            {!recharge &&
                              orgType !== 'Partner Organization' && (
                                <Grid size={{ sm: 6 }}>
                                  <Grid container direction="column">
                                    <Grid>
                                      <Box component="h5" sx={[{ mb: '0.25rem', fontWeight: 700, }, { fontSize: "1.265rem !important" }]}>
                                        Payment details
                                      </Box>
                                    </Grid>
                                    <Grid>
                                      <Box
                                        component="p"
                                        sx={{
                                          color: 'rgba(7, 9, 25, 0.5)',
                                          'html[data-color-mode="dark"] &': { color: 'rgba(255, 255, 255, 0.68)' },
                                          mb: '1.5rem',
                                        }}
                                      >
                                        Complete the fields below to
                                        setup your new organization
                                      </Box>
                                    </Grid>
                                    <Grid>
                                      <Grid
                                        container
                                        direction="column"
                                        spacing={2}
                                      >
                                        {!isCoached && (
                                          <Grid size={12}>
                                            <Box
                                              sx={[
                                                {
                                                  position: "relative",
                                                  transition: "all 0.2s ease-in-out",
                                                  "& .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
                                                  "& .MuiTextField-root": {
                                                    borderWidth: 2,
                                                    transition: "all 0.2s ease-in-out",
                                                    background: "#f8f9ff",
                                                    width: 280,
                                                    maxWidth: "100%",
                                                  },
                                                  "&:hover .MuiTextField-root": { background: "#f4f5fd", width: 210, maxWidth: "100%" },
                                                  "&.is-active .MuiTextField-root": { background: "#fff", borderColor: "#368cff" },
                                                },
                                                {
                                                  "& .MuiTextField-root": { background: "#fff", borderColor: "#368cff" },
                                                },
                                                { width: '100%' },
                                              ]}
                                            >
                                              <TextField
                                                variant="outlined"
                                                placeholder="Add Promo Code"
                                                size="small"
                                                id="input-with-icon-textfield22-1"
                                                value={coupon}
                                                onChange={(event) =>
                                                  setCoupon(
                                                    event.target
                                                      .value,
                                                  )
                                                }
                                                fullWidth={true}
                                                style={{
                                                  width: '100%',
                                                }}
                                                disabled={includes(
                                                  subscriptionPlan,
                                                  'group-coaching',
                                                )}
                                                slotProps={{
                                                  input: {
                                                    endAdornment: (
                                                      <InputAdornment position="end">
                                                        {coupon && (
                                                          <Button
                                                            onClick={() =>
                                                              checkCoupon(
                                                                coupon,
                                                              )
                                                            }
                                                          >
                                                            Apply
                                                          </Button>
                                                        )}
                                                      </InputAdornment>
                                                    ),
                                                  },
                                                }}
                                              />
                                            </Box>
                                          </Grid>
                                        )}
                                        {isCoached && (
                                          <Grid>
                                            <FormControl
                                              fullWidth
                                              variant="outlined"
                                              margin="dense"
                                            >
                                              <InputLabel id="payment-plans-label">
                                                Payment Plans
                                              </InputLabel>
                                              <Select
                                                labelId="payment-plans-label"
                                                value={
                                                  paymentIntervals
                                                }
                                                onChange={(event) =>
                                                  setPaymentIntervals(
                                                    event.target
                                                      .value,
                                                  )
                                                }
                                                label="Payment Plans"
                                              >
                                                <MenuItem value={1}>
                                                  One Payment
                                                </MenuItem>
                                                <MenuItem value={2}>
                                                  Two Payments
                                                </MenuItem>
                                                <MenuItem value={3}>
                                                  Three Payments
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                            {paymentIntervals > 1 && (
                                              <Typography
                                                style={{
                                                  marginLeft: 12,
                                                  fontSize: 13,
                                                }}
                                              >
                                                Selecting Two or Three
                                                Payments splits the
                                                coaching fee into
                                                multiple monthly
                                                payments. The next
                                                months payment will be
                                                the partial payment of
                                                the coaching fee plus
                                                the monthly
                                                subscription amount
                                              </Typography>
                                            )}
                                          </Grid>
                                        )}
                                        <Grid size={12}>
                                          <Elements
                                            stripe={stripePromise}
                                          >
                                            <PaymentForm
                                              createToken={
                                                createToken
                                              }
                                              //setElement={setElement}
                                              setResetToken={
                                                setResetToken
                                              }
                                              autoFocus
                                            />
                                          </Elements>
                                          <Typography
                                            style={{
                                              color: 'red',
                                              fontSize: 13,
                                              marginTop: 3,
                                              marginLeft: 12,
                                              fontWeight: 400,
                                            }}
                                            id="card-errors"
                                            role="alert"
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              )}
                            <Grid
                              size={{ sm: 6 }}
                              sx={{ display: 'flex' }}
                            >
                              <Box
                                sx={[
                                  {
                                    border: "1px solid rgba(79, 80, 98, 0.3)",
                                    boxShadow: "0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)",
                                    overflow: "visible",
                                    "& .card-header": { display: "flex", alignItems: "center" },
                                  },
                                  {
                                    p: '1.5rem',
                                    backgroundColor: '#f8f9ff',
                                    width: '100%',
                                    'html[data-color-mode="dark"] &': { backgroundColor: '#2a2a2a !important' },
                                  },
                                ]}
                              >
                              <Grid container>
                                <Grid size={12}>
                                  <Box component="h5" sx={[{ mb: '0.25rem', fontWeight: 700, }, { fontSize: "1.265rem !important" }]}>
                                    Purchase details
                                  </Box>
                                </Grid>
                                <Grid>
                                  <Box
                                    component="p"
                                    sx={{
                                      color: 'rgba(7, 9, 25, 0.5)',
                              'html[data-color-mode="dark"] &': { color: 'rgba(255, 255, 255, 0.68)' },
                                      mb: '1.5rem',
                                    }}
                                  >
                                    Please review the purchase details
                                    below before pressing Finish
                                  </Box>
                                </Grid>
                                <Grid size={12}>
                                  <Grid container direction="column">
                                    <Grid>
                                      <Grid
                                        container
                                        sx={{
                                          justifyContent:
                                            'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Grid>
                                          <Typography>
                                            Organization Name
                                          </Typography>
                                        </Grid>
                                        <Grid>
                                          <Typography>
                                            {name}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    <Grid>
                                      <Grid
                                        container
                                        sx={{
                                          justifyContent:
                                            'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Grid>
                                          <Typography>
                                            Plan Type
                                          </Typography>
                                        </Grid>
                                        <Grid>
                                          <Typography>
                                            {planBilling === 'yearly'
                                              ? 'Annual '
                                              : 'Monthly '}
                                            {
                                              startCase(
                                                subscriptionPlan,
                                              ).split(' ')[0]
                                            }
                                            {' Plan'}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    <Grid>
                                      <Grid
                                        container
                                        sx={{
                                          justifyContent:
                                            'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Grid>
                                          <Typography>
                                            Subtotal
                                          </Typography>
                                        </Grid>
                                        <Grid>
                                          {/* <Typography>{currencyFormat2.format(planPrice)}/{planBilling === 'yearly' ? 'yr' : 'mo'}</Typography> */}
                                          <Typography>
                                            {currencyFormat2.format(
                                              isCoached
                                                ? entrySubscriptionPlan.price /
                                                    100
                                                : planPrice,
                                            )}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    {(couponAmount ||
                                      couponPercent) && (
                                      <Grid>
                                        <Grid
                                          container
                                          sx={{
                                            justifyContent:
                                              'space-between',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Grid>
                                            <Typography
                                              style={{
                                                color: '#46de64',
                                              }}
                                            >
                                              Promo Code
                                            </Typography>
                                          </Grid>
                                          <Grid>
                                            <Typography
                                              style={{
                                                color: '#46de64',
                                              }}
                                            >
                                              -
                                              {currencyFormat2.format(
                                                couponAmount
                                                  ? couponAmount / 100
                                                  : (couponPercent /
                                                      100) *
                                                      (isCoached
                                                        ? entrySubscriptionPlan.price /
                                                          100
                                                        : planPrice),
                                              )}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    )}
                                    <Grid>
                                      <Grid
                                        container
                                        sx={{
                                          justifyContent:
                                            'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Grid>
                                          <Typography
                                            sx={{ fontWeight: 700 }}
                                          >
                                            Total due today
                                          </Typography>
                                        </Grid>
                                        <Grid>
                                          {/* <Typography>{currencyFormat2.format(planPrice - (couponAmount ? couponAmount / 100 : (couponPercent / 100 * planPrice)))}/{planBilling === 'yearly' ? 'yr' : 'mo'}</Typography> */}
                                          <Typography
                                            sx={{ fontWeight: 700 }}
                                          >
                                            {currencyFormat2.format(
                                              (isCoached
                                                ? entrySubscriptionPlan.price /
                                                  100
                                                : planPrice) -
                                                (couponAmount
                                                  ? couponAmount / 100
                                                  : (couponPercent /
                                                      100) *
                                                    (isCoached
                                                      ? entrySubscriptionPlan.price /
                                                        100
                                                      : planPrice)),
                                            )}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    <Grid>
                                      <Typography
                                        style={{ fontWeigh: 900 }}
                                      >
                                        Next payment due a{' '}
                                        {planBilling === 'monthly'
                                          ? 'month'
                                          : 'year'}{' '}
                                        from now.
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Container>
                    )}
                  </div>
                  // </ValidatorForm>
                )}
              </div>
              {/* </Card> */}
            </DialogContent>
            <DialogActions sx={{ backgroundColor: '#f8f9fe', 'html[data-color-mode="dark"] &': { backgroundColor: '#1e1e1e !important' } }}>
              {activeStep !== 0 && activeStep !== steps.length && (
                <Button
                  /*disabled={activeStep === 0}*/
                  onClick={handleBack}
                  sx={[{
                    boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
                    color: "#fff",
                    backgroundColor: "#368cff",
                    backgroundImage: "none",
                    borderColor: "#368cff",
                    "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
                    "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
                    "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
                    "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                    "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
                  }, { fontWeight: 700 }]}
                >
                  Back
                </Button>
              )}
              {activeStep === 0 && (
                <Button
                  /*disabled={activeStep === 0}*/
                  onClick={handleCloseCreateNewDialog}
                  sx={[{
                    boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
                    color: "#fff",
                    backgroundColor: "#368cff",
                    backgroundImage: "none",
                    borderColor: "#368cff",
                    "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
                    "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
                    "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
                    "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                    "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
                  }, { fontWeight: 700 }]}
                >
                  Cancel
                </Button>
              )}
              {activeStep === steps.length && (
                <Button
                  /*disabled={activeStep === 0}*/
                  onClick={handleCloseCreateNewDialog}
                  sx={[{
                    boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
                    color: "#fff",
                    backgroundColor: "#368cff",
                    backgroundImage: "none",
                    borderColor: "#368cff",
                    "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
                    "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
                    "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
                    "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                    "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
                  }, { fontWeight: 700 }]}
                >
                  Close
                </Button>
              )}
              {activeStep !== steps.length && (
                <Button
                  disabled={
                  circle ||
                  (activeStep === steps.length - 1 &&
                  !token &&
                  !recharge &&
                  orgType !== 'Partner Organization') ||
                  (activeStep === steps.length - 2 && !selectedPlan)
                  }
                  type="submit"
                  sx={[{
                    boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
                    color: "#fff",
                    backgroundColor: "#368cff",
                    backgroundImage: "none",
                    borderColor: "#368cff",
                    "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
                    "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
                    "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
                    "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                    "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
                  }, { fontWeight: 700 }]}
                  >
                  {activeStep === steps.length - 1
                    ? 'Finish'
                    : 'Next'}
                </Button>
              )}
            </DialogActions>
          </BlockUi>
        </ValidatorForm>
      </Dialog>

      <ContactDialog
        open={openContact}
        handleClose={() => setOpenContact(false)}
        affiliate={affiliate?.memberValue}
        session={props.session}
      />

      {/* <Alert
      icon={false}
      variant="filled"
      severity="success">
      This is a success alert - check it out!
    </Alert> */}
    </>
  )
}

export default withSession(CreateNewOrgComponent)
